import { Tooltip } from '@mantine/core'
import {
  getSoilDeficitZone,
  soilDeficitZoneColor,
  soilDeficitZoneLabel,
} from 'App/dashboards/_utils/soilDeficit'
import { colors, dashboardColors } from 'settings/colors'

// Dark (deep red / orange) backgrounds need light text; the lighter bands read better with dark text.
const LIGHT_TEXT_ZONES = new Set(['extremeDeficit', 'highDeficit'])
const formatPercent = (value: number): string => `${Math.round(value * 10) / 10}%`

// Shared with the chart modal's reading badge, which is drawn in the chip's shape.
export const SOIL_DEFICIT_CHIP_RADIUS = 8

/**
 * The chip's colours for a reading. Exported because the chart modal colours its Root Zone Avg badge
 * on the same bands, in the DOM and again in the SVG it exports, so a colour means the same thing in
 * all three. Returns null when the value falls in no band, i.e. there is nothing to colour.
 */
export const getSoilDeficitChipColors = (
  value: number,
  madValue?: number,
): { background: string; color: string } | null => {
  const zone = getSoilDeficitZone(value, madValue)

  if (!zone) return null

  return {
    background: soilDeficitZoneColor[zone],
    color: LIGHT_TEXT_ZONES.has(zone) ? colors.white : colors.midnight,
  }
}

// The "Current" soil-moisture value as a colored deficit chip, matching the map's AWC deficit bands.
// Hover shows the zone label ("High Deficit", …). MAD (management-allowable-depletion) shifts the bands.
export const SoilDeficitChip = ({ value, madValue }: { value: number | null; madValue?: number }) => {
  if (value === null) return <span css={{ color: colors.grey800 }}>{'-'}</span>

  const zone = getSoilDeficitZone(value, madValue)
  const chipColors = getSoilDeficitChipColors(value, madValue)

  const chip = (
    <span
      css={{
        display: 'inline-block',
        background: chipColors?.background ?? 'transparent',
        borderRadius: SOIL_DEFICIT_CHIP_RADIUS,
        padding: '4px 10px',
        fontSize: 14,
        fontWeight: 500,
        // Signals the hover tooltip — without it the chip reads as plain text.
        cursor: 'pointer',
        color: chipColors?.color ?? colors.midnight,
      }}
    >
      {formatPercent(value)}
    </span>
  )

  if (!zone) return chip

  // The design's tooltip rather than the browser's: dark, light text, and an arrow pointing down at
  // the chip. A native `title` can't be styled and waits out the OS delay before appearing.
  return (
    <Tooltip
      label={soilDeficitZoneLabel(zone)}
      position="top"
      withArrow
      // Measured off the design node: a 6px radius and a 12x6 arrow. Mantine defaults to 4 for both,
      // which renders noticeably squarer and with a smaller point. arrowSize is the side of the
      // rotated square, so 8 gives the 11.3x5.7 triangle the design draws.
      radius={6}
      arrowSize={8}
      styles={{
        tooltip: {
          background: dashboardColors.tooltipBg,
          color: dashboardColors.tooltipText,
          fontSize: 12,
          fontWeight: 400,
          padding: '6px 10px',
        },
        arrow: { background: dashboardColors.tooltipBg },
      }}
    >
      {chip}
    </Tooltip>
  )
}
